/**
 * AccountTree custom hooks.
 *
 * Extracted from AccountTree.tsx to keep the main component under 50 lines.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { getBatchAccountBalances } from '../../services/account-service'

import type { Account, AccountBalance } from '../../services/account-service'
import type { Money } from '../../types/common'

// =============================================================================
// Balances fetch
// =============================================================================

export interface UseAccountBalancesReturn {
  balances: Map<string, AccountBalance>
  fetchingRef: React.MutableRefObject<Set<string>>
}

export function useAccountBalances(orgId: string | undefined, accountsMap: Map<string, Account>): UseAccountBalancesReturn {
  const [balances, setBalances] = useState<Map<string, AccountBalance>>(new Map())
  const fetchingRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!orgId) { return }
    const toFetch = Array.from(accountsMap.keys())
      .filter(id => !balances.has(id) && !fetchingRef.current.has(id))
    if (toFetch.length === 0) { return }

    toFetch.forEach(id => fetchingRef.current.add(id))

    // Single batched request for every not-yet-fetched account, instead of
    // one GetAccountBalance round trip per row.
    void getBatchAccountBalances(orgId, toFetch)
      .then(res => res.data?.balances ?? {})
      .catch((): Record<string, AccountBalance> => ({}))
      .then(fetched => {
        setBalances(prev => {
          const next = new Map(prev)
          for (const id of toFetch) {
            const balance = fetched[id]
            if (balance) { next.set(id, balance) }
            fetchingRef.current.delete(id)
          }
          return next
        })
      })
  // balances intentionally omitted — checked synchronously via .has(); fetchingRef guards against double-fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsMap, orgId])

  return { balances, fetchingRef }
}

// =============================================================================
// Rolled-up balances
// =============================================================================

export function useRolledUpBalances(
  accountsMap: Map<string, Account>,
  childrenMap: Map<string, string[]>,
  balances: Map<string, AccountBalance>,
  baseCurrency: string,
): Map<string, Money | null> {
  return useMemo(() => {
    function sumSubtree(id: string): number | null {
      const children = childrenMap.get(id) ?? []
      if (children.length === 0) {
        const b = balances.get(id)
        return b ? Number(b.balance.amount) : null
      }
      let total = 0
      let hasAny = false
      for (const childId of children) {
        const child = sumSubtree(childId)
        if (child !== null) { total += child; hasAny = true }
      }
      return hasAny ? total : null
    }

    const result = new Map<string, Money | null>()
    for (const [id, account] of accountsMap) {
      const amount = sumSubtree(id)
      result.set(id, amount !== null ? { amount, currency: account.currency ?? baseCurrency } : null)
    }
    return result
  }, [balances, accountsMap, childrenMap, baseCurrency])
}
